export enum FormVariant {
  Mailchimp = 'mailchimp',
  Hubspot = 'hubspot',
  Static = 'static',
  None = 'none',
}

export enum StaticFormVariant {
  BusinessUnitChargingWeb2Case = 'businessUnitChargingWeb2case',
  BusinessUnitSolutionsWeb2Case = 'businessUnitSolutionsWeb2case',
  Complaint = 'complaint',
  ContactCH_DE = 'contactCH_DE',
  ContactEN = 'contactEN',
  ETContact = 'etContact',
  ETWeb2CaseContact = 'etWeb2caseContact',
  ETWeb2CaseSupport = 'etWeb2caseSupport',
  Payment = 'payment',
  ProductSupport = 'productSupport',
  ProductSupportUSA_EN = 'productSupportUSA_EN',
  Shipping = 'shipping',
  SupportForEvDriversWeb2Case = 'supportForEvDriversWeb2Case',
  Web2Case = 'web2case',
}

export interface MailchimpParameters {
  type: FormVariant.Mailchimp;
  params: {
    subdomain: string;
    u: string;
    id: string;
  };
}

export type HubspotFormId = string;

export type HubspotPortalId = number;

/**
 * Keep in sync with HubspotRegion in hubspot-proxy plugin enum. We cannot import it to the plugin space.
 *
 * @see plugins/hubspot-proxy
 */
export enum HubspotRegion {
  Europe = 'eu1',
  NorthAmerica = 'na1',
}

export interface HubspotParameters {
  type: FormVariant.Hubspot;
  params: {
    hubspotFormId: HubspotFormId;
    hubspotRegion: HubspotRegion;
    hubspotPortalId: HubspotPortalId;

    /**
     * Determines whether the form should track each successful submission as a conversion/lead in Tracify.
     *
     * @default false
     */
    shouldTrackConversions?: boolean;

    useNativeHubSpotEmbed?: boolean;
  };
}

export interface StaticFormParameters {
  type: FormVariant.Static;
  params: {
    adminEmail: string;
    form: StaticFormVariant;
  };
}

export interface NoneParameters {
  type: FormVariant.None;
}

export interface MailchimpApiResponse {
  result: 'success' | 'error';
  msg: string;
  [key: string]: any;
}

export type FormParameters = MailchimpParameters | HubspotParameters | StaticFormParameters;

export const isHubspotParameters = (params?: any): params is HubspotParameters => {
  return !!params && params.type === FormVariant.Hubspot && !!params.params.hubspotFormId;
};

export const isMailchimpParameters = (params?: any): params is MailchimpParameters => {
  return !!params && params.type === FormVariant.Mailchimp;
};

export const isStaticFormParameters = (params?: any): params is StaticFormParameters => {
  return !!params && params.type === FormVariant.Static && !!params.params.form;
};

export const isNoneParameters = (params?: any): params is NoneParameters => {
  return params === null || (!!params && params.type === FormVariant.None);
};

export const FORM_VARIANT_TO_TYPE_GUARD_MAP: Record<FormVariant, (params?: FormParameters) => boolean> = {
  [FormVariant.Mailchimp]: isMailchimpParameters,
  [FormVariant.Hubspot]: isHubspotParameters,
  [FormVariant.Static]: isStaticFormParameters,
  [FormVariant.None]: isNoneParameters,
};

export interface StaticFormPayload {
  fields: Array<{
    /**
     * Human-readable, formatted label
     */
    label: string;

    /**
     * Field name
     */
    name: string;

    /**
     * Human-readable, formatted value
     */
    value: string;
  }>;

  formParameters: FormParameters;
  gRecaptchaResponse: string;
  locale: string;
  pagePath: string;
}
